import { sumBy } from 'lodash-es'
import { useState } from 'react'

import type { FullShipment, ShippingLabelSize } from '@fv/client-types'

import { List } from '@/components/List'
import { PageHeader } from '@/components/PageHeader'
import { Pinline } from '@/components/Pinline'
import {
  composeShipmentLocationSummary,
  totalPieceCount,
} from '@/utils/shipmentFuncs'

import type { ShipmentContentProps } from '../shipment-details-panel/types'
import { useLabelSettings } from './hooks'
import { LabelOption } from './LabelOption'
import { labelOptions } from './labelConfig'

const rollLabels = labelOptions.filter(o => o.type === 'roll')
const sheetLabels = labelOptions.filter(o => o.type === 'sheet')

export type ShipmentLabelsProps = Omit<ShipmentContentProps, 'load'> & {
  load: Pick<
    FullShipment,
    'bol' | 'items' | 'loadId' | 'locations' | 'workflow'
  >
  onSubmit?: (props: {
    count: number
    loadId: string
    size: ShippingLabelSize
    start?: number
  }) => void
}

export const ShipmentLabels = ({
  load,
  onSubmit,
  setActiveSlider,
}: ShipmentLabelsProps) => {
  const { bol, items, loadId, locations } = load
  const settings = useLabelSettings(load)
  const [activeLabel, setActiveLabel] = useState<ShippingLabelSize>(
    settings.labelSize,
  )

  const bolNumber = bol?.bolNumber
  const onClose = () => setActiveSlider(undefined)
  const pieceQuantity = totalPieceCount({ items })
  const routeLabel = composeShipmentLocationSummary(locations)
  const unitQuantity = sumBy(items, 'quantity') || 1

  return (
    <div className="h-full overflow-hidden flex flex-col">
      <PageHeader
        title={
          <>
            Labels for {bolNumber && `BOL# ${bolNumber} - `}
            {routeLabel}
          </>
        }
      />

      <div className="mb-2 flex flex-1 items-stretch overflow-auto -mx-6 px-6">
        <div className="flex-1">
          <h6 className="mb-0">Sheet labels</h6>
          <Pinline className="mt-2 mb-4" />
          <List className="shipping-label-icon-ctn">
            {sheetLabels.map(o => (
              <LabelOption
                active={o.size === activeLabel}
                onClose={onClose}
                config={o}
                defaultCount={settings.labelCount}
                key={o.size}
                loadId={loadId}
                pieceQuantity={pieceQuantity}
                setActive={setActiveLabel}
                unitQuantity={unitQuantity}
                onSubmit={onSubmit}
              />
            ))}
          </List>
        </div>
        <div className="border-l border-fv-gray mx-8 my-0" />
        <div className="flex-1">
          <h6 className="mb-0">Roll labels</h6>
          <Pinline className="mt-2 mb-4" />
          <List className="shipping-label-icon-ctn flex-1">
            {rollLabels.map(o => (
              <LabelOption
                active={o.size === activeLabel}
                onClose={onClose}
                config={o}
                defaultCount={settings.labelCount}
                key={o.size}
                loadId={loadId}
                pieceQuantity={pieceQuantity}
                setActive={setActiveLabel}
                unitQuantity={unitQuantity}
                onSubmit={onSubmit}
              />
            ))}
          </List>
        </div>
      </div>
    </div>
  )
}
